import { useEffect, useMemo, useRef, useCallback, useState } from "react";
import { cn, formatCurrency } from "@/lib/utils";
import { useProduct } from "@/features/product/product-context";
import { MARKETS } from "@/solana/constants";
import { useMarketStat } from "@/backend/hooks/use-market-stats";
import { useMarkets as useDynamicMarkets } from "@/domains/markets";
import { useMarkets } from "@/solana/hooks/use-markets";
import { useMarketPrice, useOraclePrice } from "@/backend/store";

// Custom hook for drag-to-scroll functionality on desktop
const useDragScroll = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const container = containerRef.current;
    if (!container) return;
    
    setIsDragging(true);
    startX.current = e.pageX - container.offsetLeft;
    scrollLeft.current = container.scrollLeft;
    container.style.cursor = 'grabbing';
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grab';
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grab';
    }
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const container = containerRef.current;
    if (!container) return;
    
    const x = e.pageX - container.offsetLeft;
    const walk = (x - startX.current) * 1.5; // Scroll speed multiplier
    container.scrollLeft = scrollLeft.current - walk;
  }, [isDragging]);

  return {
    containerRef,
    dragHandlers: {
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseLeave,
      onMouseMove: handleMouseMove,
    },
    isDragging,
  };
};

export const TickerStats = () => {
  const { product } = useProduct();
  const { markets: dynamicMarkets } = useDynamicMarkets();
  
  // Check if current product is a perpetual market (check both hardcoded and dynamic)
  const isPerpMarket = useMemo(() => {
    if (!product?.id) return false;
    // Check hardcoded markets
    if (product.id in MARKETS) return true;
    // Check dynamic markets
    return dynamicMarkets.some(m => m.symbol === product.id);
  }, [product?.id, dynamicMarkets]);

  if (isPerpMarket) {
    return <PerpetualStats product={product} />;
  }

  return <SpotStats product={product as any} />;
};

// Perpetual market stats component
const PerpetualStats = ({ product }: any) => {
  const prevMarketPrice = useRef(0);
  const prevSentiment = useRef<string | undefined>(undefined);
  const { containerRef, dragHandlers, isDragging } = useDragScroll();
  
  // Get markets data for 24H change
  const { markets } = useMarkets();
  
  // Get real market stats from backend (includes open interest and funding)
  const marketStat = useMarketStat(product?.id || "BTC-USD");

  // Subscribe to mark price from Zustand store (efficient selector)
  const marketPrice = useMarketPrice(product?.id || "BTC-USD");
  
  // Subscribe to oracle price from Zustand store
  const oraclePrice = useOraclePrice(product?.id || "BTC-USD");

  // Price tracking for sentiment color
  useEffect(() => {
    if (marketPrice === 0 || marketPrice === prevMarketPrice.current) return;

    prevSentiment.current =
      marketPrice > prevMarketPrice.current ? "text-bid-foreground" : "text-ask-foreground";
    prevMarketPrice.current = marketPrice;
  }, [marketPrice]);

  const stats = useMemo(() => {
    if (!product) return;

    // Use store prices (not localStorage)
    const marketPriceClassName =
      marketPrice === prevMarketPrice.current
        ? prevSentiment.current
        : marketPrice > prevMarketPrice.current
        ? "text-bid-foreground"
        : "text-ask-foreground";

    // Use real data from backend market stats
    const openInterestUsd = marketStat?.openInterestUsd ?? 0;
    const volume24hUsd = marketStat?.volume24hUsd ?? 0;
    const fundingRate = marketStat?.fundingRate ?? 0;
    const nextFundingTime = marketStat?.nextFundingTime ?? Date.now();

    // Oracle price for display (from Pyth - used for funding)
    const oraclePriceDisplay =
      oraclePrice > 0 ? formatCurrency(oraclePrice, product.quote.precision) : "-";
    
    // 24H change - get from markets data
    const marketData = markets?.find(m => m.symbol === product.id);
    const priceChange24h = marketData?.priceChange24h;
    const priceChangePercent24h = marketData?.priceChangePercent24h;
    
    const changeDisplay = formatPriceChange(priceChange24h, priceChangePercent24h);
    const changeClassName = getPriceChangeColor(priceChangePercent24h);
    
    return {
      marketPrice: formatCurrency(marketPrice, product.quote.precision),
      marketPriceClassName,
      oraclePrice: oraclePriceDisplay,
      change: changeDisplay,
      changeClassName,
      volume: formatOpenInterestUsd(volume24hUsd),
      openInterest: formatOpenInterestUsd(openInterestUsd),
      funding: formatFundingCountdown(fundingRate, nextFundingTime),
      fundingClassName: fundingRate >= 0 ? "text-bid-foreground" : "text-ask-foreground",
    };
  }, [product, marketPrice, oraclePrice, marketStat, markets]);

  return (
    <div 
      ref={containerRef}
      className={cn(
        "flex items-center gap-6 overflow-x-auto scrollbar-hide cursor-grab select-none",
        isDragging && "cursor-grabbing"
      )}
      {...dragHandlers}
    >
      <Stat
        label="Market Price"
        value={stats?.marketPrice}
        className={cn("flex-shrink-0", stats?.marketPriceClassName)}
      />
      <Stat
        label="Oracle Price"
        value={stats?.oraclePrice}
        className="flex-shrink-0"
      />
      <Stat
        label="24H Change"
        value={stats?.change}
        className={cn("flex-shrink-0", stats?.changeClassName)}
      />
      <Stat
        label="24H Volume"
        value={stats?.volume}
        className="flex-shrink-0"
      />
      <Stat
        label="Open Interest"
        value={stats?.openInterest}
        className="flex-shrink-0"
      />
      <Stat
        label="Funding / Countdown"
        value={stats?.funding}
        className={cn("flex-shrink-0", stats?.fundingClassName)}
      />
    </div>
  );
};

// Spot market stats component (simplified - no real-time spot market data yet)
const SpotStats = ({ product }: any) => {
  const { containerRef, dragHandlers, isDragging } = useDragScroll();

  const stats = useMemo(() => {
    if (!product) return;

    return {
      lastPrice: "-",
      change: "-",
      volume: "-",
    };
  }, [product]);

  return (
    <div 
      ref={containerRef}
      className={cn(
        "flex items-center gap-6 overflow-x-auto scrollbar-hide cursor-grab select-none",
        isDragging && "cursor-grabbing"
      )}
      {...dragHandlers}
    >
      <Stat
        label="Last Price"
        value={stats?.lastPrice}
        className="flex-shrink-0"
      />
      <Stat
        label="24H Change"
        value={stats?.change}
        className="flex-shrink-0"
      />
      <Stat
        label="24H Volume"
        value={stats?.volume}
        className="flex-shrink-0"
      />
    </div>
  );
};

type StatProps = {
  label: string;
  value?: string;
  className?: string;
};

const Stat = ({ label, value, className }: StatProps) => {
  return (
    <div className={className}>
      <div className="flex flex-col gap-[2px]">
        <span className="text-[11px] text-muted-foreground">{label}</span>
        <span className={cn("text-[11px] font-normal", className)}>
          {value || "-"}
        </span>
      </div>
    </div>
  );
};

// Ticker data is now provided by backend WebSocket
// which broadcasts real-time prices from the configured market data provider

// Helper function to format open interest in USD (full numbers with commas)
const formatOpenInterestUsd = (value: number): string => {
  if (value === 0) return "$0";
  
  return "$" + new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Helper function to format funding rate and countdown
const formatFundingCountdown = (fundingRate: number, nextFundingTime: number): string => {
  const fundingRatePercent = (fundingRate * 100).toFixed(4);
  const sign = fundingRate >= 0 ? "+" : "";
  
  const now = Date.now();
  const diff = nextFundingTime - now;
  
  if (diff <= 0) return `${sign}${fundingRatePercent}% / Soon`;
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  return `${sign}${fundingRatePercent}% / ${hours}h ${minutes}m`;
};

// Helper function to format price change with absolute and percentage
const formatPriceChange = (absolute?: number, percent?: number): string => {
  if (absolute === undefined || percent === undefined) {
    if (percent !== undefined) {
      const sign = percent >= 0 ? "+" : "";
      return `${sign}${percent.toFixed(2)}%`;
    }
    return "-";
  }
  
  const sign = absolute >= 0 ? "+" : "";
  const absFormatted = Math.abs(absolute) >= 1000 
    ? `${sign}${(absolute / 1000).toFixed(1)}K`
    : `${sign}${absolute.toFixed(2)}`;
  const percentSign = percent >= 0 ? "+" : "";
  return `${absFormatted} (${percentSign}${percent.toFixed(2)}%)`;
};

// Helper function to get color based on price change
const getPriceChangeColor = (percent?: number): string => {
  if (percent === undefined || percent === null || percent === 0) return "text-muted-foreground";
  return percent > 0 ? "text-bid-foreground" : "text-ask-foreground";
};
